import type { NodeEntry } from 'slate';

import type { RenderInlineOptions } from './inline';
import { renderInlineFns } from './inline';
import type { RenderTextOptions } from './text';
import { renderText } from './text';
import {
  getCheckboxInfo,
  getCheckboxSelectedAnswerIdsFromField,
  isRenderModelCheckboxOptionEntry,
  isRenderModelSignatureFieldEntry,
  Queries,
} from '../../slate-api';
import type { RenderModelInputField, RenderModelSignatureField } from '../../slate-api';
import {
  activeInputFieldBackgroundColor,
  defaultInputFieldBackgroundColor,
  disabledCheckboxColor,
  inputFieldBorderColor,
} from '../colors';
import type { RenderContext } from '../engine/render-context';

export function renderInputField(
  renderContext: RenderContext,
  rMInputFieldEntry: NodeEntry<RenderModelInputField>,
  options: RenderInlineOptions
) {
  if (isRenderModelSignatureFieldEntry(rMInputFieldEntry)) {
    renderSignatureField(renderContext, rMInputFieldEntry, options);
  } else {
    renderPlainInputField(renderContext, rMInputFieldEntry, options);
  }
}

function renderSignatureField(
  renderContext: RenderContext,
  rMSignatureFieldEntry: NodeEntry<RenderModelSignatureField>,
  options: RenderTextOptions
) {
  const [rMSignatureField, rMSignatureFieldPath] = rMSignatureFieldEntry;
  const { signatureImgId } = rMSignatureField;
  const { imgHeightPx: heightPx, imgWidthPx: widthPx } =
    Queries.getSignatureImageDimensions(rMSignatureField);
  const { cMPath } = renderContext;
  const { baselinePx } = options;
  const topPx = baselinePx - heightPx;
  const isSigned = !!signatureImgId;
  const isSelected = renderContext.isSelectionInsideCMNode();

  if (isSigned) {
    /**
     * `contain` is a safety net: `signOrClearSignatureField` stamps matching dims, so
     * it only kicks in for older fields whose aspect drifted. The stamp image already
     * carries the bracket and labels baked in by `drawFramedSigPreview`, so the
     * placeholder isn't drawn on top of it.
     */
    renderContext.drawImage(0, topPx, widthPx, heightPx, {
      fit: 'contain',
      imageId: signatureImgId,
    });
  } else if (cMPath) {
    renderContext.renderReactComponent(
      0,
      topPx,
      'SignatureFieldPlaceholder',
      { clippable: true, key: `${rMSignatureFieldPath}-placeholder`, zIndex: 2 },
      { isSelected, signatureFieldEntry: [rMSignatureField, cMPath] }
    );
  }

  renderContext.renderChildren(
    rMSignatureFieldEntry,
    { text: renderText },
    {
      ...options,
      inlineVoid: {
        caret: 'hidden',
        overrideWidthPx: Queries.getSignatureFieldLayoutWidthPx(rMSignatureField),
      },
    }
  );

  renderContext.setMouseCursor(0, topPx, widthPx, heightPx, {
    cursor: 'pointer',
    key: `${rMSignatureFieldPath}-cursor`,
  });

  if (isSelected && cMPath)
    renderContext.renderReactComponent(
      0,
      topPx,
      'SignatureFieldPopoverAnchor',
      { clippable: false, key: `${rMSignatureFieldPath}-popover` },
      { signatureFieldEntry: [rMSignatureField, cMPath] }
    );
}

function renderPlainInputField(
  renderContext: RenderContext,
  rMInputFieldEntry: NodeEntry<RenderModelInputField>,
  options: RenderInlineOptions
) {
  const { editor } = renderContext;
  const isPDFExport = renderContext.withContext('isPDFExport');
  const isReadOnly = renderContext.withContext('isReadOnly');
  const [, rMInputFieldPath] = rMInputFieldEntry;
  const { baselinePx, lineMaxDescentPx } = options;
  const isSelected = renderContext.isSelectionInsideCMNode();
  const isCheckboxOption = isRenderModelCheckboxOptionEntry(rMInputFieldEntry);

  let renderChildrenOptions: RenderTextOptions = isCheckboxOption
    ? options
    : {
        ...options,
        customHighlight: isPDFExport
          ? undefined
          : {
              color: isSelected
                ? activeInputFieldBackgroundColor.toString()
                : defaultInputFieldBackgroundColor.toString(),
            },
        inlineVoid: {
          caret: 'hidden',
          showText: true,
        },
        overrideCursor: {
          cursor: 'pointer',
          key: `${rMInputFieldPath}-cursor`,
        },
      };

  if (isCheckboxOption) {
    const [checkboxOption] = rMInputFieldEntry;
    const isChecked = getCheckboxInfo([checkboxOption, []]).isChecked;
    const maxRequired = renderContext.withContextSelector(
      'checkboxMaxRequiredByFieldId',
      map => map.get(checkboxOption.fieldId) ?? null
    );
    const selectedCount = renderContext.withEditor(
      editor => getCheckboxSelectedAnswerIdsFromField(editor, checkboxOption.fieldId).length
    );
    if (!isChecked && maxRequired !== null && selectedCount >= maxRequired) {
      renderChildrenOptions = {
        ...renderChildrenOptions,
        overrideColor: disabledCheckboxColor.toString(),
      };
    }

    if (isReadOnly && !isPDFExport) {
      renderChildrenOptions = {
        ...renderChildrenOptions,
        overrideCursor: {
          cursor: 'pointer',
          key: `${rMInputFieldPath}-cursor`,
        },
      };
    }
  }

  renderContext.renderChildren(rMInputFieldEntry, renderInlineFns, renderChildrenOptions);

  // Underline
  if (!isPDFExport)
    renderContext.drawHorizontalLine({
      color: inputFieldBorderColor.toString(),
      // Approximately matches the CSS-based dash used for signature fields, depending on the browser
      dashPattern: isSelected ? [6, 4] : [3, 2],
      endX: Queries.getRMNodeWidthPx(editor, rMInputFieldEntry),
      startX: 0,
      thickness: isSelected ? 2 : 1,
      y: baselinePx + lineMaxDescentPx,
    });
}
